"use client"

import { SearchIcon } from "@/app/icons/search"
import { Skeleton } from "@rarible_int/anaconda_ui_components"
import dynamic from "next/dynamic"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useCallback, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { BluredOverlay } from "../components/blured-overlay"
import { useWalletConnect } from "../wallet-connect/hooks"
import { Banner } from "./banner/banner"
import { ScrollAwareBanner } from "./banner/scroll-aware-banner"
import { GachaLogo, Logo } from "./logo"
import { SearchReatom } from "./search/search.reatom"

const ProfileWidgetReatom = dynamic(
  () =>
    import("./profile-widget.reatom").then((mod) => mod.ProfileWidgetReatom),
  {
    ssr: false,
    loading: () => <ProfileWidgetSkeleton />,
  },
)

function ProfileWidgetSkeleton() {
  // Simple skeleton - don't check wallet state during loading
  // The actual ProfileWidgetReatom will render the correct state
  return (
    <div className="flex items-center gap-x-4 md:gap-x-6">
      <Skeleton className="h-9 w-[85px]" />
      <Skeleton className="h-6 w-6 md:hidden" />
    </div>
  )
}

interface Props {
  className?: string
  navigation: React.ReactNode
  // Provided by the parent component, because it's server component which depends on feature flag
  rewardsFeature: React.ReactNode
  disableBlur?: boolean // New prop to disable BluredOverlay's default blur
  hideBanner?: boolean // New prop to hide the banner completely
  customSearch?: React.ReactNode // Optional custom search component
  hideSearch?: boolean
}

export function HeaderContentReatom({
  className,
  navigation,
  rewardsFeature,
  disableBlur = false,
  hideBanner = false,
  customSearch,
  hideSearch = false,
}: Props) {
  const pathname = usePathname()
  const isGachaPath = pathname === "/gacha" || pathname.startsWith("/gacha/")
  const shouldHideSearch = hideSearch || isGachaPath
  const shouldHideBanner = hideBanner || isGachaPath
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false)

  const handleOpenMobileSearch = useCallback(() => {
    setIsMobileSearchOpen(true)
  }, [])

  const handleCloseMobileSearch = useCallback(() => {
    setIsMobileSearchOpen(false)
  }, [])

  const overlayRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const content = contentRef.current
      if (content?.contains(e.target as Node)) {
        return
      }
      setIsMobileSearchOpen(false)
    },
    [],
  )

  const content = (
    <>
      {!shouldHideBanner && (disableBlur ? <ScrollAwareBanner /> : <Banner />)}
      <div className="flex min-h-[theme('spacing.header-height')] items-center justify-between gap-x-6 gap-y-4 px-4 py-3 md:px-8">
        <div className="flex items-center gap-x-8 xl:min-w-[400px]">
          <Link href={isGachaPath ? "/gacha" : "/"}>
            {isGachaPath ? <GachaLogo /> : <Logo />}
          </Link>
          {navigation}
        </div>
        {!shouldHideSearch && (
          <div className="hidden max-w-[400px] flex-grow lg:block">
            {customSearch || <SearchReatom className="w-full max-w-[400px]" />}
          </div>
        )}
        <div className="flex items-center justify-end gap-x-6 md:min-w-[250px] xl:min-w-[400px]">
          {!shouldHideSearch && (
            <button
              type="button"
              onClick={handleOpenMobileSearch}
              aria-label="Open search"
              title="Search"
              className="inline-flex h-6 w-6 items-center justify-center text-foreground hover:text-foreground/80 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand lg:hidden"
            >
              <SearchIcon className="h-6 w-6" />
            </button>
          )}
          {rewardsFeature}
          <ProfileWidgetReatom />
        </div>
      </div>
    </>
  )

  if (disableBlur) {
    return (
      <div className={className} data-testid="header">
        {content}
      </div>
    )
  }

  return (
    <BluredOverlay className={className} data-testid="header">
      {content}
      {!shouldHideSearch &&
        isMobileSearchOpen &&
        typeof window !== "undefined" &&
        createPortal(
          <div
            ref={overlayRef}
            className="fixed inset-0 top-0 left-0 z-[1000] m-0 flex h-screen w-screen translate-x-0 translate-y-0 flex-col bg-transparent p-0 backdrop-blur-sm"
            onClick={handleOverlayClick}
          >
            <div
              ref={contentRef}
              className="p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <SearchReatom className="w-full" autoFocus />
            </div>
          </div>,
          document.body,
        )}
    </BluredOverlay>
  )
}
